import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { ClientLayout } from "../../components/layout/ClientLayout";

interface IClientProfile {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: "client";
  created_at: string;
}

export function ClientProfile() {
  const navigate = useNavigate();
  const { profile: authProfile } = useAuth();

  const [profile, setProfile] = useState<IClientProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
  });

  useEffect(() => {
    if (authProfile) {
      setProfile({
        id: authProfile.id,
        email: authProfile.email || "",
        name: authProfile.name || "",
        phone: authProfile.phone || "",
        role: authProfile.role as "client",
        created_at: authProfile.created_at || new Date().toISOString(),
      });

      setFormData({
        name: authProfile.name || "",
        phone: authProfile.phone || "",
      });

      setLoading(false);
    }
  }, [authProfile]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from("users")
        .update({
          name: formData.name,
          phone: formData.phone || null,
        })
        .eq("id", profile.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              name: formData.name,
              phone: formData.phone,
            }
          : null,
      );

      setSuccess(true);
      setEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update profile";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name,
        phone: profile.phone || "",
      });
    }
    setEditing(false);
  };

  if (loading) {
    return (
      <ClientLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </ClientLayout>
    );
  }

  if (!profile) {
    return (
      <ClientLayout>
        <Box sx={{ p: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/client")}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Alert severity="error">Failed to load profile</Alert>
        </Box>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/client")}
          >
            Back to Dashboard
          </Button>
          <Box sx={{ flex: 1 }} />
          {!editing && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Profile Information
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Profile updated successfully!
              </Alert>
            )}

            {/* Profile Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                mb: 4,
                pb: 3,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "primary.main",
                  fontSize: "2rem",
                }}
              >
                {profile.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {profile.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {profile.email}
                </Typography>
                <Chip
                  label="Client"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>

            {/* Form */}
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Full Name
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  name="name"
                  disabled={!editing}
                  variant={editing ? "outlined" : "standard"}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  value={profile.email}
                  disabled
                  variant="standard"
                  helperText="Email cannot be changed"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Phone Number
                </Typography>
                <TextField
                  fullWidth
                  value={formData.phone}
                  onChange={handleInputChange}
                  name="phone"
                  disabled={!editing}
                  placeholder="(555) 123-4567"
                  variant={editing ? "outlined" : "standard"}
                />
              </Box>

              {/* Member Since */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Member Since
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(profile.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Box>

              <Divider />

              {/* Action Buttons */}
              {editing && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </ClientLayout>
  );
}
